import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Upload,
  Database,
  FileText,
  Loader2,
  Trash2,
  Search,
  AlertCircle,
  CheckCircle2,
  CheckCircle,
  Download,
  Info,
  RefreshCw,
  Calendar,
  ExternalLink,
  FileCode,
  Eye,
  X,
  Clock,
  Tag,
  Building2,
  UserCircle
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const getStatusBadge = (status) => {
  switch (status) {
    case "registered":
      return <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-mono uppercase">Registered</span>;
    case "examination":
      return <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs font-mono uppercase">Examination</span>;
    case "published":
      return <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 border border-purple-200 text-xs font-mono uppercase">Published</span>;
    case "opposed":
      return <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 border border-orange-200 text-xs font-mono uppercase">Opposed</span>;
    case "dead":
      return <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 text-xs font-mono uppercase">Dead</span>;
    default:
      return <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-mono uppercase">{status}</span>;
  }
};

export const UKIPOImport = () => {
  const fileInputRef = useRef(null);
  const xmlFileInputRef = useRef(null);
  const [stats, setStats] = useState({ total_applications: 0, by_status: {}, recent_imports: 0 });
  const [applications, setApplications] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fetchingJournal, setFetchingJournal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [fetchStatus, setFetchStatus] = useState(null);
  const [triggeringAutoFetch, setTriggeringAutoFetch] = useState(false);
  
  // New state for detail modal and sample applications
  const [selectedApp, setSelectedApp] = useState(null);
  const [appDetail, setAppDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sampleApps, setSampleApps] = useState([]);
  const [latestJournal, setLatestJournal] = useState(null);
  const [searching, setSearching] = useState(false);
  const [totalApplications, setTotalApplications] = useState(0);
  const [latestUpload, setLatestUpload] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, appsRes, journalsRes, fetchStatusRes, latestUploadRes] = await Promise.all([
        axios.get(`${API}/ukipo/stats`),
        axios.get(`${API}/ukipo/applications?limit=100`),
        axios.get(`${API}/ukipo/journals`),
        axios.get(`${API}/ukipo/fetch-status`).catch(() => ({ data: null })),
        axios.get(`${API}/ukipo/latest-upload`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data.applications || []);
      setTotalApplications(appsRes.data.total || 0);
      setJournals(journalsRes.data || []);
      if (fetchStatusRes.data) setFetchStatus(fetchStatusRes.data);
      if (latestUploadRes.data) setLatestUpload(latestUploadRes.data);
      
      // Find the latest journal with imported records
      const journalData = journalsRes.data || [];
      const importedJournals = journalData.filter(j => j.fetched && j.records_imported > 0);
      if (importedJournals.length > 0) {
        const latest = importedJournals[0];
        setLatestJournal(latest);
        
        // Fetch sample applications from the latest journal
        try {
          const sampleRes = await axios.get(`${API}/ukipo/sample-applications?journal_id=${latest.journal_id}&limit=5`);
          setSampleApps(sampleRes.data.applications || []);
        } catch (err) {
          console.error("Error fetching sample applications:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching UK IPO data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Server-side search for applications
  const handleServerSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      // Reset to default view
      try {
        const appsRes = await axios.get(`${API}/ukipo/applications?limit=100`);
        setApplications(appsRes.data.applications || []);
        setTotalApplications(appsRes.data.total || 0);
      } catch (error) {
        console.error("Error resetting applications:", error);
      }
      return;
    }
    
    setSearching(true);
    try {
      const params = new URLSearchParams({
        search: term,
        limit: "100"
      });
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      const response = await axios.get(`${API}/ukipo/applications?${params}`);
      setApplications(response.data.applications || []);
      setTotalApplications(response.data.total || 0);
    } catch (error) {
      console.error("Error searching applications:", error);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  }, [statusFilter]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleServerSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, handleServerSearch]);

  // Fetch application detail when selecting an app
  const handleViewDetail = async (app) => {
    setSelectedApp(app);
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/ukipo/applications/${app.id}`);
      setAppDetail(response.data);
    } catch (error) {
      console.error("Error fetching application detail:", error);
      toast.error("Failed to load application details");
      // Set basic detail from the app object
      setAppDetail({ application: app, related_alerts: [], status_history: [] });
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetailModal = () => {
    setSelectedApp(null);
    setAppDetail(null);
  };

  const handleTriggerAutoFetch = async () => {
    setTriggeringAutoFetch(true);
    try {
      const response = await axios.post(`${API}/ukipo/trigger-auto-fetch`);
      toast.success(response.data.message || "Auto-fetch triggered");
      // Refresh data after a delay
      setTimeout(fetchData, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger auto-fetch");
    } finally {
      setTriggeringAutoFetch(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.csv', '.json', '.xml', '.zip'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExt)) {
      toast.error("Please select a CSV, JSON, XML or ZIP file");
      return;
    }

    setUploading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/ukipo/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported_records} records`);
        fetchData();
      } else {
        toast.error("Import completed with errors");
      }
    } catch (error) {
      console.error("Import error:", error);
      const errorDetail = error.response?.data?.detail || "Failed to import file";
      toast.error(errorDetail);
      setImportResult({
        success: false,
        total_records: 0,
        imported_records: 0,
        skipped_records: 0,
        errors: [errorDetail]
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleXmlFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xml')) {
      toast.error("Please select an XML file (jnl.xml from UK IPO)");
      return;
    }

    setUploading(true);
    setImportResult(null);
    toast.info("Uploading and importing... This may take up to 90 seconds for large journals.");

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/ukipo/import-xml`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for large XML files
      });

      setImportResult(response.data);
      
      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported_records} trademark applications`);
        fetchData();
      } else {
        toast.error(response.data.errors?.[0] || "Import completed with issues");
      }
    } catch (error) {
      console.error("XML import error:", error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Import timed out. The data may still be importing - please refresh the page in a minute to check.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to import XML file.");
      }
    } finally {
      setUploading(false);
      if (xmlFileInputRef.current) {
        xmlFileInputRef.current.value = '';
      }
    }
  };

  const handleFetchJournal = async (journalId) => {
    setFetchingJournal(journalId);
    try {
      const response = await axios.post(`${API}/ukipo/fetch-journal/${journalId}`, {}, {
        timeout: 300000 // 5 minute timeout
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Journal fetch error:", error);
      toast.error(error.response?.data?.detail || "Failed to fetch journal");
    } finally {
      setFetchingJournal(null);
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/ukipo/applications`);
      toast.success("All UK IPO data cleared");
      setShowClearDialog(false);
      fetchData();
    } catch (error) {
      console.error("Error clearing data:", error);
      toast.error("Failed to clear data");
    }
  };

  const handleDeleteApplication = async (id) => {
    try {
      await axios.delete(`${API}/ukipo/applications/${id}`);
      toast.success("Application deleted");
      setApplications(applications.filter(app => app.id !== id));
      setStats(prev => ({ ...prev, total_applications: prev.total_applications - 1 }));
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Failed to delete application");
    }
  };

  // Client-side filtering for status only (search is server-side)
  const filteredApplications = applications.filter(app => {
    const matchesStatus = statusFilter === "all" || app.trademark_status === statusFilter;
    return matchesStatus;
  });

  const downloadTemplate = (format) => {
    if (format === 'csv') {
      const csvContent = `application_number,name,applicant,filing_date,categories,description,status
UK00123456,Example Brand,Example Company Ltd,2024-01-15,"9,42",Software and technology services,published
UK00123457,Sample Mark,Sample Holdings,2024-01-16,"25,35",Clothing and retail services,registered`;
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ukipo_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const jsonContent = {
        applications: [
          {
            application_number: "UK00123456",
            name: "Example Brand",
            applicant: "Example Company Ltd",
            filing_date: "2024-01-15",
            categories: ["9", "42"],
            description: "Software and technology services",
            status: "published"
          }
        ]
      };
      
      const blob = new Blob([JSON.stringify(jsonContent, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ukipo_template.json';
      a.click();
      URL.revokeObjectURL(url);
    }
    toast.success(`Downloaded ${format.toUpperCase()} template`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ukipo-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="ukipo-import-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
            UK IPO Data
          </h1>
          <p className="text-slate-500 mt-1">Import UK Trade Marks Journal XML data weekly</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowClearDialog(true)}
            className="btn-secondary text-red-600 hover:bg-red-50"
            disabled={stats.total_applications === 0}
            data-testid="clear-all-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            CLEAR ALL
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <Database className="w-5 h-5 text-slate-600" />
            </div>
            <span className="text-sm text-slate-500 uppercase tracking-wider">Total</span>
          </div>
          <p className="stat-value text-slate-900">{stats.total_applications}</p>
        </motion.div>

        <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <div className="w-10 h-10 bg-purple-100 flex items-center justify-center mb-3">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <span className="text-sm text-slate-500 uppercase tracking-wider">Published</span>
          <p className="stat-value text-purple-600 mt-1">{stats.by_status?.published || 0}</p>
        </motion.div>

        <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="w-10 h-10 bg-blue-100 flex items-center justify-center mb-3">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <span className="text-sm text-slate-500 uppercase tracking-wider">Examination</span>
          <p className="stat-value text-blue-600 mt-1">{stats.by_status?.examination || 0}</p>
        </motion.div>

        <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <span className="text-sm text-slate-500 uppercase tracking-wider">Registered</span>
          <p className="stat-value text-emerald-600 mt-1">{stats.by_status?.registered || 0}</p>
        </motion.div>

        <motion.div className="swiss-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="w-10 h-10 bg-orange-100 flex items-center justify-center mb-3">
            <AlertCircle className="w-5 h-5 text-orange-600" />
          </div>
          <span className="text-sm text-slate-500 uppercase tracking-wider">Opposed</span>
          <p className="stat-value text-orange-600 mt-1">{stats.by_status?.opposed || 0}</p>
        </motion.div>
      </div>

      {/* Latest Upload Info Banner */}
      {latestUpload?.has_uploads && (
        <motion.div 
          className="swiss-card mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          data-testid="latest-upload-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 flex items-center justify-center rounded-lg">
              <Upload className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 font-semibold uppercase tracking-wider">
                  Most Recent Upload
                </span>
              </div>
              <h3 className="font-bold text-lg text-slate-900" data-testid="latest-upload-filename">
                {latestUpload.filename}
              </h3>
              <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {latestUpload.uploaded_at ? new Date(latestUpload.uploaded_at).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Unknown'}
                </span>
                <span className="flex items-center gap-1">
                  <Database className="w-4 h-4" />
                  {(latestUpload.records_imported || 0).toLocaleString()} records imported
                </span>
                {latestUpload.records_updated > 0 && (
                  <span className="flex items-center gap-1">
                    <RefreshCw className="w-4 h-4" />
                    {latestUpload.records_updated.toLocaleString()} updated
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Sample Applications from Latest Journal */}
      {sampleApps.length > 0 && latestJournal && (
        <motion.div 
          className="swiss-card mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.21 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Sample from Journal {latestJournal.journal_id}</h3>
                <p className="text-xs text-slate-500">
                  {latestJournal.records_imported?.toLocaleString()} applications imported
                </p>
              </div>
            </div>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 font-mono">
              Latest Import
            </span>
          </div>
          <div className="space-y-2">
            {sampleApps.map((app, idx) => (
              <div 
                key={app.id || idx}
                className="flex items-center justify-between p-3 bg-white border border-emerald-200 hover:border-emerald-300 cursor-pointer transition-colors"
                onClick={() => handleViewDetail(app)}
                data-testid={`sample-app-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{app.name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs font-mono text-slate-500">{app.application_number}</span>
                    <span className="text-xs text-slate-400">{app.applicant}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(app.trademark_status)}
                  <Eye className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">
            Click any application to view full details
          </p>
        </motion.div>
      )}

      {/* Automated Fetch Status */}
      <motion.div 
        className="swiss-card mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Automated Weekly Import</h3>
              <p className="text-sm text-slate-600 mt-1">
                UK IPO journals are automatically fetched every <strong>Friday at 6:00 PM UK time</strong>.
              </p>
              {fetchStatus && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-500">
                    <span className="font-medium">Current Journal:</span> {fetchStatus.current_journal_id}
                  </p>
                  <p className="text-xs text-slate-500">
                    <span className="font-medium">Next Auto-Fetch:</span> {new Date(fetchStatus.next_scheduled_fetch).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                  {fetchStatus.last_successful_import && (
                    <p className="text-xs text-emerald-600">
                      <span className="font-medium">Last Import:</span> {fetchStatus.last_successful_import.records_imported} records from journal {fetchStatus.last_successful_import.journal_id}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={handleTriggerAutoFetch}
            disabled={triggeringAutoFetch}
            className="btn-primary whitespace-nowrap"
            data-testid="trigger-auto-fetch-btn"
          >
            {triggeringAutoFetch ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Fetching...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Fetch Latest Now
              </>
            )}
          </Button>
        </div>
      </motion.div>

      {/* UK IPO Journal Section */}
      <motion.div 
        className="swiss-card mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-5 h-5 text-slate-600" />
          <h2 className="text-xl font-heading font-bold text-slate-900">UK IPO Trade Marks Journal - XML Data Files</h2>
        </div>
        
        <p className="text-sm text-slate-500 mb-4">
          Download the <strong>jnl.xml</strong> data file from each weekly journal and upload it to import trademark applications.
          The XML file contains all trademark data in ST.96 format.
        </p>

        {/* Journal List */}
        <div className="border border-slate-200 divide-y divide-slate-200 mb-4">
          {journals.slice(0, 4).map((journal) => (
            <div key={journal.journal_id} className="flex items-center justify-between p-3 hover:bg-slate-50">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${journal.fetched ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                <div>
                  <p className="font-mono text-sm font-semibold text-slate-900">Journal {journal.journal_id}</p>
                  <p className="text-xs text-slate-500">{journal.publication_date}</p>
                </div>
                {journal.fetched && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5">
                    {journal.records_imported} imported
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={journal.xml_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                  data-testid={`download-xml-${journal.journal_id}`}
                >
                  <FileCode className="w-3 h-3" />
                  XML
                </a>
                <Button
                  size="sm"
                  onClick={() => handleFetchJournal(journal.journal_id)}
                  disabled={fetchingJournal === journal.journal_id || journal.fetched}
                  className={journal.fetched ? "btn-secondary opacity-50" : "btn-primary"}
                  data-testid={`fetch-journal-${journal.journal_id}`}
                >
                  {fetchingJournal === journal.journal_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : journal.fetched ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Manual XML Upload */}
        <div className="bg-purple-50 border border-purple-200 p-4">
          <div className="flex items-start gap-3 mb-3">
            <FileCode className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-semibold text-purple-900">Upload UK IPO Journal XML (jnl.xml)</p>
              <p className="text-sm text-purple-700">
                Download the <strong>jnl.xml</strong> file from{' '}
                <a href="https://www.ipo.gov.uk/t-tmj/" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
                  UK IPO Trade Marks Journal <ExternalLink className="w-3 h-3" />
                </a>
                {' '}and upload it here to import trademark data.
              </p>
            </div>
          </div>
          <input
            ref={xmlFileInputRef}
            type="file"
            accept=".xml"
            onChange={handleXmlFileSelect}
            className="hidden"
            data-testid="xml-file-input"
          />
          <Button
            onClick={() => xmlFileInputRef.current?.click()}
            disabled={uploading}
            className="btn-primary bg-purple-600 hover:bg-purple-700"
            data-testid="upload-xml-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            UPLOAD XML FILE
          </Button>
        </div>
      </motion.div>

      {/* Import Result */}
      {importResult && (
        <motion.div 
          className={`swiss-card mb-6 ${importResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2 mb-2">
            {importResult.success ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-orange-600" />
            )}
            <span className="font-semibold">{importResult.success ? 'Import Successful' : 'Import Failed'}</span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Total Records:</span>
              <span className="ml-2 font-mono">{importResult.total_records}</span>
            </div>
            <div>
              <span className="text-slate-500">Imported:</span>
              <span className="ml-2 font-mono text-emerald-600">{importResult.imported_records}</span>
            </div>
          </div>
          {importResult.errors && importResult.errors.length > 0 && (
            <div className="mt-3 pt-3 border-t border-orange-200">
              <p className="text-sm font-semibold text-orange-700 mb-1">Errors:</p>
              <ul className="text-xs text-orange-600 space-y-1">
                {importResult.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>
      )}

      {/* Info Box */}
      <motion.div 
        className="swiss-card bg-blue-50 border-blue-200 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-900">Weekly Import Process</p>
            <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
              <li>Visit the UK IPO Trade Marks Journal page each Friday</li>
              <li>Click the "XML" button for the latest journal to download <strong>jnl.xml</strong></li>
              <li>Upload the XML file using the button above</li>
            </ol>
            <p className="text-sm text-blue-600 mt-2">
              The XML file contains all published trademark applications with their current status.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Manual File Upload Section - PROMINENT */}
      <motion.div 
        className="swiss-card mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center rounded-lg">
            <Upload className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-slate-900">Upload UK IPO Journal File</h2>
            <p className="text-sm text-slate-500">Import trademark applications from downloaded journal files</p>
          </div>
        </div>

        <div className="bg-white border-2 border-dashed border-emerald-300 p-6 text-center mb-4 rounded-lg hover:border-emerald-500 transition-colors cursor-pointer" onClick={() => fileInputRef.current?.click()}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json,.xml,.zip"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <Upload className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-slate-700 font-medium mb-1">Click to upload UK IPO Journal file</p>
          <p className="text-slate-500 text-sm mb-3">Supports: ZIP (jnl.zip), XML (jnl.xml), CSV, JSON</p>
          <Button
            variant="default"
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={uploading}
            data-testid="upload-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                SELECT FILE TO IMPORT
              </>
            )}
          </Button>
        </div>

        {/* Import Result */}
        {importResult && (
          <div className={`rounded-lg p-4 mb-4 ${importResult.success ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'}`}>
            <div className="flex items-center gap-2 mb-2">
              {importResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <span className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {importResult.success ? 'Import Successful!' : 'Import Completed with Issues'}
              </span>
            </div>
            <div className="text-sm space-y-1">
              <p><strong>Total Records:</strong> {importResult.total_records}</p>
              <p><strong>Imported:</strong> {importResult.imported_records}</p>
              {importResult.skipped_records > 0 && (
                <p><strong>Skipped:</strong> {importResult.skipped_records}</p>
              )}
              {importResult.status_changes_detected > 0 && (
                <p className="text-blue-700"><strong>Status Changes Detected:</strong> {importResult.status_changes_detected}</p>
              )}
              {importResult.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium text-red-700">Errors:</p>
                  <ul className="list-disc list-inside text-red-600 text-xs">
                    {importResult.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 pt-2 border-t border-emerald-200">
          <span className="text-sm text-slate-500 pt-2">Sample Templates:</span>
          <Button variant="outline" size="sm" onClick={() => downloadTemplate('csv')} className="text-xs mt-2">
            <Download className="w-3 h-3 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadTemplate('json')} className="text-xs mt-2">
            <Download className="w-3 h-3 mr-1" /> JSON
          </Button>
        </div>
      </motion.div>

      {/* Applications List */}
      <motion.div 
        className="swiss-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-heading font-bold text-slate-900">Imported Applications</h2>
          <span className="text-sm text-slate-500">{totalApplications.toLocaleString()} total</span>
        </div>

        {/* Search and Filters - Always visible */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-grow min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, applicant, number or representative..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 pl-10 pr-10 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="search-applications"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 h-10" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="registered">Registered</SelectItem>
              <SelectItem value="examination">Examination</SelectItem>
              <SelectItem value="opposed">Opposed</SelectItem>
              <SelectItem value="dead">Dead</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Applications Table */}
        {filteredApplications.length > 0 ? (
          <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="table-header text-left py-3 px-2">Application #</th>
                  <th className="table-header text-left py-3 px-2">Name</th>
                  <th className="table-header text-left py-3 px-2">Applicant</th>
                  <th className="table-header text-left py-3 px-2">Representative</th>
                  <th className="table-header text-left py-3 px-2">Status</th>
                  <th className="table-header text-left py-3 px-2">Filed</th>
                  <th className="table-header text-right py-3 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.slice(0, 50).map((app) => (
                  <tr 
                    key={app.id} 
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => handleViewDetail(app)}
                    data-testid={`ukipo-app-${app.id}`}
                  >
                    <td className="py-3 px-2 font-mono text-sm text-slate-500">
                      {app.application_number}
                    </td>
                    <td className="py-3 px-2">
                      <p className="font-semibold text-slate-900">{app.name}</p>
                      {app.categories && app.categories.length > 0 && (
                        <p className="text-xs text-slate-400">Classes: {app.categories.join(', ')}</p>
                      )}
                    </td>
                    <td className="py-3 px-2 text-sm text-slate-600">{app.applicant}</td>
                    <td className="py-3 px-2 text-sm">
                      {app.representative_name ? (
                        <div>
                          <p className="text-indigo-700 font-medium">{app.representative_name}</p>
                          {app.representative_address && (
                            <p className="text-xs text-slate-400 truncate max-w-[150px]" title={app.representative_address}>
                              {app.representative_address.split(',')[0]}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400 italic text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2">{getStatusBadge(app.trademark_status)}</td>
                    <td className="py-3 px-2 text-sm text-slate-500">{app.filing_date}</td>
                    <td className="py-3 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleViewDetail(app); }}
                          className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                          data-testid={`view-app-${app.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteApplication(app.id); }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-app-${app.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredApplications.length > 50 && (
            <p className="text-sm text-slate-400 mt-4 text-center">
              Showing 50 of {totalApplications > filteredApplications.length ? totalApplications : filteredApplications.length} applications
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
          )}
        </>
        ) : (
          <div className="text-center py-8">
            <Database className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">
              {searching ? "Searching..." : searchTerm ? `No applications found for "${searchTerm}"` : "No applications imported yet. Upload a UK IPO journal to get started."}
            </p>
            {searchTerm && (
              <p className="text-xs text-slate-400 mt-1">Try searching by trademark name, applicant, application number, or representative</p>
            )}
          </div>
        )}
      </motion.div>

      {/* Clear All Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent data-testid="clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All UK IPO Data</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {stats.total_applications} imported UK IPO applications. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearAll}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-clear-btn"
            >
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Application Detail Modal */}
      <Dialog open={!!selectedApp} onOpenChange={(open) => !open && closeDetailModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="app-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Application Details</DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : appDetail?.application ? (
            <div className="space-y-6">
              {/* Main Info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{appDetail.application.name}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    {getStatusBadge(appDetail.application.trademark_status)}
                    <span className="font-mono text-sm text-slate-500">{appDetail.application.application_number}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500 uppercase">Applicant</span>
                    </div>
                    <p className="font-medium text-slate-900">{appDetail.application.applicant}</p>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500 uppercase">Filing Date</span>
                    </div>
                    <p className="font-medium text-slate-900">{appDetail.application.filing_date || "N/A"}</p>
                  </div>
                </div>

                {/* Categories */}
                {appDetail.application.categories && appDetail.application.categories.length > 0 && (
                  <div className="p-3 bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500 uppercase">Nice Classification Classes</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {appDetail.application.categories.map((cat, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-mono">
                          Class {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {appDetail.application.description && (
                  <div className="p-3 bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500 uppercase">Description of Goods/Services</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {appDetail.application.description}
                    </p>
                  </div>
                )}

                {/* Representative/Agent Information */}
                {(appDetail.application.representative_name || appDetail.application.representative_address) && (
                  <div className="p-3 bg-indigo-50 border border-indigo-200">
                    <div className="flex items-center gap-2 mb-2">
                      <UserCircle className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs text-indigo-600 uppercase font-semibold">Representative / Agent</span>
                    </div>
                    {appDetail.application.representative_name && (
                      <p className="font-medium text-slate-900">{appDetail.application.representative_name}</p>
                    )}
                    {appDetail.application.representative_address && (
                      <p className="text-sm text-slate-600 mt-1">{appDetail.application.representative_address}</p>
                    )}
                  </div>
                )}

                {/* No Representative Notice */}
                {!appDetail.application.representative_name && !appDetail.application.representative_address && (
                  <div className="p-3 bg-slate-50 border border-slate-200">
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500 uppercase">Representative / Agent</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1 italic">
                      No representative/agent information available for this application. 
                      This data will be captured from future UK IPO journal imports.
                    </p>
                  </div>
                )}

                {/* Source & Import Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Source:</span>
                    <span className="ml-2 font-mono text-slate-700">{appDetail.application.source || "import"}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Imported:</span>
                    <span className="ml-2 text-slate-700">
                      {appDetail.application.imported_at 
                        ? new Date(appDetail.application.imported_at).toLocaleDateString('en-GB')
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Related Alerts */}
              {appDetail.related_alerts && appDetail.related_alerts.length > 0 && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    Related Alerts ({appDetail.related_alerts.length})
                  </h4>
                  <div className="space-y-2">
                    {appDetail.related_alerts.map((alert, idx) => (
                      <div key={alert.id || idx} className="flex items-center justify-between p-2 bg-amber-50 border border-amber-200 text-sm">
                        <span className="font-medium">{alert.my_trademark_name}</span>
                        <span className={`px-2 py-0.5 text-xs font-mono uppercase ${
                          alert.overall_risk_level === 'high' ? 'bg-red-100 text-red-700' :
                          alert.overall_risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {alert.overall_risk_level} risk
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status History */}
              {appDetail.status_history && appDetail.status_history.length > 0 && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    Status History
                  </h4>
                  <div className="space-y-2">
                    {appDetail.status_history.map((change, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 border border-slate-200 text-sm">
                        <span className="text-slate-400 text-xs">
                          {new Date(change.changed_at).toLocaleDateString('en-GB')}
                        </span>
                        <span className="text-slate-500">{change.old_status}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-medium text-slate-900">{change.new_status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No details available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
